// Build the hair recolour coverage from an ML portrait alpha-matte.
//
// MODNet gives a per-pixel PERSON alpha with strand-level edges and the
// background reading ~0 in the gaps between flyaway strands — exactly what the
// heuristic matte (and its `core` term) could not produce. We turn it into a
// HAIR coverage by multiplying with the eroded+feathered hair-region envelope:
//
//   coverage(p) = personAlpha(p) * envelope(p)
//
// - envelope keeps tint inside the hairline (never on face/shirt/background).
// - personAlpha kills the gaps between strands and the wisp/background edge,
//   AND keeps low-contrast hair (grey on grey) because it keys on shape, not
//   colour. This replaces the heuristic `core` (which force-tinted gaps).

import { createCanvas } from './maskUtils';

function ctxOf(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable');
  return ctx;
}

// Per-pixel product of two 0..255 alpha planes, returned 0..255.
export function combineHairAlpha(
  personAlpha: Uint8ClampedArray,
  regionAlpha: Uint8ClampedArray
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(personAlpha.length);
  for (let i = 0; i < out.length; i++) out[i] = (personAlpha[i]! * regionAlpha[i]!) / 255;
  return out;
}

function smoothstep(e0: number, e1: number, x: number): number {
  if (e1 <= e0) return x < e0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

// The one case MODNet can't separate: hair over the SHIRT COLLAR. There both
// hair and shirt are person-alpha ≈ 1 (both foreground), so the gaps between
// strands over the collar keep tinting the white shirt. The discriminator left
// is luminance: the collar is brighter than hair. Suppress coverage for bright,
// low-chroma source pixels. The ramp starts at 170 — above grey hair (~168, the
// brightest real hair, demo 50) — so it never touches hair, only the white
// shirt showing between strands.
export function collarGuard(srcLuma: number, srcChroma: number): number {
  const bright = smoothstep(170, 205, srcLuma);
  const neutral = 1 - smoothstep(22, 50, srcChroma);
  return 1 - bright * neutral; // 1 = keep, → 0 = suppress (white collar)
}

export interface PersonAlpha {
  data: Float32Array; // length aw*ah, values 0..1
  aw: number;
  ah: number;
}

// Draw a single-channel alpha plane (Float32 0..1) into a canvas's alpha channel
// at its native resolution.
function alphaToCanvas(p: PersonAlpha): HTMLCanvasElement {
  const c = createCanvas(p.aw, p.ah);
  const img = ctxOf(c).createImageData(p.aw, p.ah);
  for (let i = 0; i < p.aw * p.ah; i++) {
    img.data[i * 4] = 255;
    img.data[i * 4 + 1] = 255;
    img.data[i * 4 + 2] = 255;
    img.data[i * 4 + 3] = Math.max(0, Math.min(255, Math.round(p.data[i]! * 255)));
  }
  ctxOf(c).putImageData(img, 0, 0);
  return c;
}

function sampleAlpha(mask: HTMLCanvasElement, W: number, H: number): Uint8ClampedArray {
  const c = createCanvas(W, H);
  ctxOf(c).drawImage(mask, 0, 0, W, H); // bilinear upscale
  const data = ctxOf(c).getImageData(0, 0, W, H).data;
  const out = new Uint8ClampedArray(W * H);
  for (let i = 0; i < W * H; i++) out[i] = data[i * 4 + 3]!;
  return out;
}

// Build the W×H coverage canvas (white, alpha = coverage) — drop-in for the
// recolour layer's clip mask, same shape as buildHairCoverageMatte's output.
// `source` is the photo canvas (W×H), read to apply the collar guard.
export function buildHairAlphaCoverage(
  source: HTMLCanvasElement,
  W: number,
  H: number,
  person: PersonAlpha,
  envelope: HTMLCanvasElement
): HTMLCanvasElement {
  const personCanvas = alphaToCanvas(person);
  const personW = sampleAlpha(personCanvas, W, H);
  const envW = sampleAlpha(envelope, W, H);
  const coverage = combineHairAlpha(personW, envW);
  const src = ctxOf(source).getImageData(0, 0, W, H).data;

  const out = createCanvas(W, H);
  const img = ctxOf(out).createImageData(W, H);
  for (let i = 0; i < W * H; i++) {
    const r = src[i * 4]!;
    const g = src[i * 4 + 1]!;
    const b = src[i * 4 + 2]!;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    img.data[i * 4] = 255;
    img.data[i * 4 + 1] = 255;
    img.data[i * 4 + 2] = 255;
    img.data[i * 4 + 3] = Math.round(coverage[i]! * collarGuard(luma, chroma));
  }
  ctxOf(out).putImageData(img, 0, 0);
  return out;
}
